import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import moment from 'moment-timezone';
import { outboundLogsService } from '../services/outboundLogsService';

const DAILY_STATS_TTL_MS = 2 * 60 * 1000;
const EMPTY_TODAY_SUMMARY = {
  totalCalls: 0,
  totalCallsConnected: 0,
  totalCallsDNC: 0,
  totalCallsNoAnswerVoicemail: 0,
  totalCallsAppointments: 0,
  totalLiveTransfers: 0,
  totalCallsHumanFollowup: 0,
  percentCallsConnected: 0,
};

const buildChartData = (dailyStats) => [
  {
    label: 'Calls Connected',
    data: [{ title: 'Calls Connected', value: dailyStats.totalCallsConnected }],
  },
  {
    label: 'No Answer/Voicemail',
    data: [{ title: 'No Answer/Voicemail', value: dailyStats.totalCallsNoAnswerVoicemail }],
  },
  {
    label: 'DNC',
    data: [{ title: 'DNC', value: dailyStats.totalCallsDNC }],
  },
  {
    label: 'Human Followups',
    data: [{ title: 'Human Followups', value: dailyStats.totalCallsHumanFollowup }],
  },
  {
    label: 'Appointments Booked',
    data: [{ title: 'Appointments Booked', value: dailyStats.totalCallsAppointments }],
  },
  {
    label: 'Live Transfers',
    data: [{ title: 'Live Transfers', value: dailyStats.totalLiveTransfers }],
  },
];

export const useDailyStatsStore = create(
  devtools(
    (set, get) => ({
      loading: false,
      summary: {},
      chartData: [],
      timeZone: '',
      selectedDate: new Date(),
      lastFetched: 0,
      setTimeZone: (timeZone) => set({ timeZone }, false, 'dailyStats/setTimeZone'),
      loadDailyStats: async ({ date = new Date(), force = false } = {}) => {
        const state = get();
        const timeZone = state.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const dateKey = moment(date).format('YYYY-MM-DD');
        const currentKey = moment(state.selectedDate).format('YYYY-MM-DD');
        const cacheFresh = Date.now() - state.lastFetched < DAILY_STATS_TTL_MS;
        if (!force && state.summary?.today && currentKey === dateKey && cacheFresh) {
          return;
        }

        set({ loading: true, selectedDate: date, timeZone }, false, 'dailyStats/load:start');
        try {
          const response = await outboundLogsService.getDailySummary({ timezone: timeZone, date: dateKey });
          const summaryPayload = response?.data?.summary || response?.data?.data?.summary || {};
          const dailyStats = summaryPayload?.today || EMPTY_TODAY_SUMMARY;
          set(
            {
              summary: summaryPayload,
              chartData: buildChartData(dailyStats),
              selectedDate: date,
              loading: false,
              lastFetched: Date.now(),
            },
            false,
            'dailyStats/load:success'
          );
        } catch (error) {
          set({ loading: false }, false, 'dailyStats/load:error');
        }
      },
    }),
    { name: 'daily-stats-store' }
  )
);
